import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { getOccupants } from '../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SAMPLE_LEDGER } from './utils';
import LedgerSummaryCards from './LedgerSummaryCards';
import LedgerFilters from './LedgerFilters';
import LedgerTable from './LedgerTable';

const MemberLedgerReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { wingsDropdown, wingsDropdownLoading, flatsDropdown, flatsDropdownLoading } = useSelector(state => state.WingsAndFlatsReducers);
    const { occupantsData, loading: membersLoading } = useSelector(state => state.OccupantReducer);

    const wings = wingsDropdown || [];
    const flats = flatsDropdown || [];
    const members = occupantsData || [];

    const [selection, setSelection] = useState({ wing_id: 'all', flat_id: 'all', member_id: 'all' });
    const [filters, setFilters] = useState({ startDate: '', endDate: '', transactionType: 'all' });

    useEffect(() => {
        if (societyId) dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (selection.wing_id !== 'all') dispatch(getFlatsDropdown({ wing_id: selection.wing_id }));
    }, [dispatch, selection.wing_id]);

    useEffect(() => {
        if (selection.flat_id !== 'all') dispatch(getOccupants({ society_id: societyId, flat_id: selection.flat_id, type: 'owner' }));
    }, [dispatch, societyId, selection.flat_id]);

    const memberOptions = members.map(m => {
        const entity = m.entity || m;
        return {
            id: String(m.flat_ownership_id || entity.occupant_id),
            label: [entity.first_name, entity.last_name].filter(Boolean).join(' '),
        };
    });

    const selectedMemberLabel = memberOptions.find(m => String(m.id) === String(selection.member_id))?.label;
    const selectedWing = wings.find(w => String(w.wing_id) === String(selection.wing_id));
    const selectedFlat = flats.find(f => String(f.flat_id) === String(selection.flat_id));
    const flatNo = selectedWing && selectedFlat ? `${selectedWing.title}-${selectedFlat.flat_number}` : null;
    const hasMember = selection.member_id !== 'all';

    const filteredLedger = useMemo(() => {
        if (filters.transactionType === 'all') return SAMPLE_LEDGER;
        return SAMPLE_LEDGER.filter(row => row.isOpening || row.type === filters.transactionType);
    }, [filters.transactionType]);

    const periodTotals = useMemo(() => {
        const rows = filteredLedger.filter(r => !r.isOpening);
        return {
            debit: rows.reduce((sum, r) => sum + (r.debit || 0), 0),
            credit: rows.reduce((sum, r) => sum + (r.credit || 0), 0),
        };
    }, [filteredLedger]);

    const summary = {
        openingBalance: 2450.00,
        openingDate: 'Oct 01, 2023',
        totalDues: periodTotals.debit,
        totalPaid: periodTotals.credit,
        closingBalance: 2450.00 + periodTotals.debit - periodTotals.credit,
    };

    const handleReset = () => {
        setSelection({ wing_id: 'all', flat_id: 'all', member_id: 'all' });
        setFilters({ startDate: '', endDate: '', transactionType: 'all' });
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Member Ledger Report</h1>
                    {hasMember ? (
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                            <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-bg-blue-600 tw-text-white tw-text-[10px] tw-font-semibold tw-rounded">
                                Flat No: {flatNo}
                            </span>
                            <span className="tw-text-xs tw-text-gray-500">
                                Resident: <span className="tw-font-medium tw-text-gray-700">{selectedMemberLabel}</span>
                            </span>
                        </div>
                    ) : (
                        <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Select a member to view their ledger statement</p>
                    )}
                </div>
                {hasMember && (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors">
                            <Icon icon="mdi:printer-outline" className="tw-text-sm" />
                            Print
                        </button>
                        <button className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors">
                            <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />
                            Excel
                        </button>
                        <button className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-lg hover:tw-bg-blue-700 tw-transition-colors">
                            <Icon icon="mdi:download-outline" className="tw-text-sm" />
                            Download PDF
                        </button>
                    </div>
                )}
            </div>

            {/* Filters — member selection + date/type filters in one row */}
            <LedgerFilters
                selection={selection}
                setSelection={setSelection}
                wings={wings}
                flats={flats}
                memberOptions={memberOptions}
                wingsDropdownLoading={wingsDropdownLoading}
                flatsDropdownLoading={flatsDropdownLoading}
                membersLoading={membersLoading}
                filters={filters}
                setFilters={setFilters}
                onApply={() => console.log('Apply filters:', { ...selection, ...filters })}
                onReset={handleReset}
            />

            {hasMember ? (
                <>
                    <LedgerSummaryCards summary={summary} />
                    <LedgerTable
                        ledgerData={filteredLedger}
                        periodTotals={periodTotals}
                        closingBalance={summary.closingBalance}
                    />
                </>
            ) : (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                    <Icon icon="mdi:file-document-outline" className="tw-text-5xl tw-text-gray-200" />
                    <p className="tw-text-gray-400 tw-mt-2 tw-text-sm">Select a wing, flat, and member above to view the ledger</p>
                </div>
            )}
        </>
    );
};

export default MemberLedgerReport;
